import React, { useState, useEffect, useCallback } from 'react';
import { getLancamentos, getAnexoUrl, Lancamento } from '../services/api';
import {
    Typography, CircularProgress, Alert, Box, Divider, Chip, IconButton, Collapse,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PrintIcon from '@mui/icons-material/Print';


// Row component with collapsible details
const LancamentoRow: React.FC<{ 
    lancamento: Lancamento, 
    onDelete: (lancamento: Lancamento) => void, 
    onEdit: (lancamento: Lancamento) => void, 
    onAttach?: (lancamento: Lancamento) => void,
    onPrintReceipt?: (lancamento: Lancamento) => void,
}> = ({ lancamento, onDelete, onEdit, onAttach, onPrintReceipt }) => {
    const [open, setOpen] = useState(false);

    const formatCurrency = (value: number | undefined) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value || 0);
    };

    const formatNumber = (value: number | undefined) => {
        return new Intl.NumberFormat('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value || 0);
    };

    const formatDateWithoutTimezone = (dateString: string) => {
        if (!dateString) return '';
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(date);
    };

    const mainDescription = lancamento.items.length > 1
        ? `${lancamento.items[0].descricao} (+${lancamento.items.length - 1} itens)`
        : lancamento.items[0]?.descricao || 'Lançamento sem descrição';

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">{formatDateWithoutTimezone(lancamento.data_lancamento)}</TableCell>
                <TableCell>{lancamento.obra_nome}</TableCell>
                <TableCell>{lancamento.fornecedor_nome || '-'}</TableCell>
                <TableCell>{lancamento.investidor_nome || '-'}</TableCell>
                <TableCell>{mainDescription}</TableCell>
                <TableCell align="right">{formatCurrency(lancamento.valor_total_liquido)}</TableCell>
                <TableCell align="center"><Chip label={lancamento.status_pagamento} color={lancamento.status_pagamento === 'Pago' ? 'success' : 'warning'} size="small" /></TableCell>
                <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'nowrap', gap: 0.5 }}>
                        {onAttach && (
                            <IconButton onClick={() => onAttach(lancamento)} title="Anexar Comprovante">
                                <AttachFileIcon />
                            </IconButton>
                        )}
                        {onPrintReceipt && (
                            <IconButton 
                                onClick={() => onPrintReceipt(lancamento)} 
                                title="Imprimir Recibo / Assinatura" 
                                color="secondary"
                            >
                                <PrintIcon />
                            </IconButton>
                        )}
                        <IconButton onClick={() => onEdit(lancamento)} title="Editar" color="info">
                            <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => onDelete(lancamento)} title="Excluir" color="error">
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, padding: 2, backgroundColor: '#f9f9f9', borderRadius: 2, paddingLeft: 6 }}>
                            <Typography variant="h6" gutterBottom component="div">Detalhes do Lançamento</Typography>

                            <Typography variant="subtitle1" sx={{ mt: 2 }}>Itens</Typography>
                            <Table size="small" aria-label="items">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Fase</TableCell>
                                        <TableCell>Categoria</TableCell>
                                        <TableCell>Descrição</TableCell>
                                        <TableCell align="right">Qtd.</TableCell>
                                        <TableCell align="right">Valor Unit. Bruto</TableCell>
                                        <TableCell align="right">Desconto</TableCell>
                                        <TableCell align="right">Total Líquido</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lancamento.items.map((item: any) => {
                                        const descontoValor = item.desconto_valor || 0;
                                        const descontoPercentual = item.desconto_percentual || 0;
                                        const totalDesconto = descontoValor + (item.quantidade * item.valor_unitario * descontoPercentual / 100);

                                        return (
                                            <TableRow key={item.id}>
                                                <TableCell>{item.fase_obra_nome || '-'}</TableCell>
                                                <TableCell>{item.categoria_nome}</TableCell>
                                                <TableCell>{item.descricao}</TableCell>
                                                <TableCell align="right">{formatNumber(item.quantidade)}</TableCell>
                                                <TableCell align="right">{formatCurrency(item.valor_unitario)}</TableCell>
                                                <TableCell align="right" sx={{ color: 'error.main' }}>{formatCurrency(totalDesconto)}</TableCell>
                                                <TableCell align="right">{formatCurrency(item.valor_liquido_proporcional)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: '1 1 50%' }}>
                                    <Typography variant="subtitle1">Detalhes Adicionais</Typography>
                                    <Typography variant="body2"><strong>Fornecedor:</strong> {lancamento.fornecedor_nome || 'N/A'}</Typography>
                                    <Typography variant="body2"><strong>Nº Nota:</strong> {lancamento.numero_nota || 'N/A'}</Typography>
                                    <Typography variant="body2"><strong>Observações:</strong> {lancamento.observacoes || 'Nenhuma'}</Typography>
                                </Box>
                                <Box sx={{ flex: '1 1 40%', textAlign: 'right' }}>
                                    <Typography variant="subtitle2">Valor Total Bruto: {formatCurrency(lancamento.valor_total_bruto)}</Typography>
                                    <Typography variant="body2" color="error">Descontos: {formatCurrency((lancamento.valor_total_bruto || 0) - (lancamento.valor_total_liquido || 0))}</Typography>
                                    <Typography variant="h6">Valor Total Líquido: {formatCurrency(lancamento.valor_total_liquido)}</Typography>
                                </Box>
                            </Box>

                            {lancamento.anexos && lancamento.anexos.length > 0 && (
                                <Box mt={2}>
                                    <Typography variant="subtitle1">Anexos</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {lancamento.anexos.map((anexo: any) => (
                                            <Chip
                                                key={anexo.id}
                                                label={anexo.nome_original}
                                                component="a"
                                                href={getAnexoUrl('anexos', anexo.caminho_arquivo)}
                                                target="_blank"
                                                clickable
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

interface LancamentosTableProps {
    filters?: {
        obraId?: number;
        fornecedorId?: number;
        startDate?: string;
        endDate?: string;
        search?: string;
        missingAttachments?: boolean;
    };
    onEdit: (lancamento: Lancamento) => void;
    onAttach?: (lancamento: Lancamento) => void;
    onDelete: (lancamento: Lancamento) => void;
    onPrintReceipt?: (lancamento: Lancamento) => void;
    refreshTrigger?: number; // Prop to force refresh
}

const LancamentosTable: React.FC<LancamentosTableProps> = ({ filters, onEdit, onAttach, onDelete, onPrintReceipt, refreshTrigger }) => {
    const [lancamentos, setLancamentos] = useState<Lancamento[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    const fetchLancamentos = useCallback(async () => {
        setLoading(true);
        try {
            const { data, total } = await getLancamentos({
                page,
                pageSize: rowsPerPage,
                obra_id: filters?.obraId,
                fornecedor_id: filters?.fornecedorId,
                startDate: filters?.startDate,
                endDate: filters?.endDate,
                search: filters?.search,
                missingAttachments: filters?.missingAttachments
            });

            // Client-side filtering for search/date if RPC doesn't support it yet (temporary solution)
            // Or better, I should update the RPC. But let's first get the table working.

            setLancamentos(data || []);
            setTotalRows(total);
        } catch (err) {
            setError('Erro ao buscar dados dos lançamentos.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, filters?.obraId, filters?.fornecedorId, filters?.search, filters?.startDate, filters?.endDate, filters?.missingAttachments]);

    useEffect(() => {
        fetchLancamentos();
    }, [fetchLancamentos, refreshTrigger]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Paper>
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table aria-label="collapsible table">
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>Data</TableCell>
                            <TableCell>Obra</TableCell>
                            <TableCell>Fornecedor</TableCell>
                            <TableCell>Investidor</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell align="right">Valor Líquido</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {lancamentos.map((lanc) => (
                            <LancamentoRow key={lanc.id} lancamento={lanc} onDelete={onDelete} onEdit={onEdit} onAttach={onAttach} onPrintReceipt={onPrintReceipt} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalRows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Linhas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
        </Paper>
    );
};

export default LancamentosTable;
